import { HttpStatusCode, TypeComponentNoti } from "@/enums/enums";
import { GetKeepLogin, MerchantId, SetCookiesAccessToken, SetCookiesRefreshToken } from "@/helper/authenticationHelper";
import { i18n } from "@/i18n";
import API from "@/service/Index";
import store from "@/store/index";

const MAX_PRIVATE_FILE_TOKEN_REFRESH = 2;

// เช็คจาก host จริง ไม่เทียบกับ env เพราะรูปเก่าใน DB อาจชี้ storage account ที่เลิกใช้แล้ว
const IsAzureBlobStorageUrl = (url: string) => {
  try {
    return new URL(url).hostname.endsWith(".blob.core.windows.net");
  } catch (error) {
    return false;
  }
};

// ถ้า env ใส่ slash ท้ายมาแล้วไม่ตัด จะได้ /file// ซึ่งพัง
const GetFileServiceBaseUrl = () => {
  const base = process.env.VUE_APP_PeakFileService || "";
  return base.endsWith("/") ? base.slice(0, -1) : base;
};

const GetFileServiceRootUrl = () => GetFileServiceBaseUrl().replace(/\/file$/, "");

// env ยังว่างให้ถือว่าไม่มีไฟล์ private ทุกจุดจะคง URL เดิมไว้แทนที่จะยิง path ที่ผิด
export const IsPrivateFileUrl = (url: string) => {
  if (!url || url.startsWith("data:")) return false;
  const fileServiceRoot = GetFileServiceRootUrl();
  if (!fileServiceRoot) return false;
  return IsAzureBlobStorageUrl(url) || url.startsWith(fileServiceRoot);
};

export const ConvertFileUrlFromBlobStorageToFileService = (url: string) => {
  if (!url) return "";
  const fileServiceUrl = GetFileServiceBaseUrl();
  if (!fileServiceUrl || !IsAzureBlobStorageUrl(url)) {
    return url;
  }
  const { pathname, search } = new URL(url);
  return `${fileServiceUrl}${pathname}${search}`;
};

// ผลลัพธ์เท่ากับตัวด้านบนทุกกรณี คงไว้ให้ชื่อตรงกับ repo อื่น ยังไม่มีที่เรียกใช้ในโปรเจกต์นี้
export const ConvertBlobUrlToFileServiceOpenUrl = (url: string) => {
  if (!url) return url;
  const fileServiceUrl = GetFileServiceBaseUrl();
  if (!fileServiceUrl || !IsAzureBlobStorageUrl(url)) {
    return url;
  }
  const { pathname, search } = new URL(url);
  return `${fileServiceUrl}${pathname}${search}`;
};

// muze ไม่มี static CDN แยก อ่าน asset ของระบบผ่าน file service ได้เพราะ Content/** เป็น Public
export const GetStaticFileServiceUrl = (relativePath: string) => {
  if (!relativePath) return "";
  const base = process.env.VUE_APP_PeakFileService || process.env.VUE_APP_PeakBlobStorage || "";
  if (!base) return "";
  const normalizedBase = base.endsWith("/") ? base.slice(0, -1) : base;
  const normalizedPath = relativePath.startsWith("/") ? relativePath.slice(1) : relativePath;
  return `${normalizedBase}/${normalizedPath}`;
};

// เฉพาะลายเซ็น backend มีกฎ Deny ถ้า path ไม่มี base64(merchantId) คั่นก่อนชื่อไฟล์
export const InsertMerchantIdToFileUrl = (url: string) => {
  if (!url || url.startsWith("data:")) return url;
  const fileServiceRoot = GetFileServiceRootUrl();

  const target = ConvertFileUrlFromBlobStorageToFileService(url);
  if (!fileServiceRoot || !target.startsWith(fileServiceRoot)) return url;

  const merchantId = MerchantId();
  if (!merchantId) return target;

  let emi: string;
  try {
    emi = btoa(String(merchantId));
  } catch (error) {
    return target;
  }

  const [pathPart, queryPart] = target.split("?");
  const segments = pathPart.split("/");
  const filename = segments.pop() as string;
  if (segments[segments.length - 1] !== emi) {
    segments.push(emi);
  }
  segments.push(filename);
  const rebuilt = segments.join("/");
  return queryPart ? `${rebuilt}?${queryPart}` : rebuilt;
};

const RefreshAccessTokenForPrivateFile = async () => {
  const refreshResult = await API.AuthenService.DoGetNewAccessTokenByRefreshToken();
  if (refreshResult && refreshResult.status && refreshResult.status.IsSuccess()) {
    const keepLogin = GetKeepLogin();
    SetCookiesAccessToken(refreshResult.data.access_token);
    SetCookiesRefreshToken(refreshResult.data.refresh_token, keepLogin);
    return true;
  }
  return false;
};

export const FetchWithTokenRefresh = async (url: string, options = {}) => {
  const fetchOptions = { ...options, credentials: "include" as const };
  let response = await fetch(url, fetchOptions);

  let refreshCount = 0;
  while (response.status === HttpStatusCode.Unauthorized && refreshCount < MAX_PRIVATE_FILE_TOKEN_REFRESH) {
    refreshCount += 1;
    let refreshed = false;
    try {
      refreshed = await RefreshAccessTokenForPrivateFile();
    } catch (error) {
      refreshed = false;
    }
    if (!refreshed) break;
    response = await fetch(url, fetchOptions);
  }

  if (response.status === HttpStatusCode.Unauthorized) {
    store.dispatch(
      "Alert/SetAlertMessage",
      {
        alertMessage: i18n.t("Cannot_Access_Image_From_Link"),
        typeMessage: TypeComponentNoti.Error,
      },
      { root: true }
    );
    throw new Error("Session expired - unable to load private file");
  }

  return response;
};

const fileBlobInFlight = new Map<string, Promise<Blob>>();

// หลายคอมโพเนนต์ขอรูปเดียวกันพร้อมกัน ให้แชร์ request เดิมแทนการยิงซ้ำทุกตัว
const FetchFileBlob = (url: string) => {
  const requestUrl = ConvertFileUrlFromBlobStorageToFileService(url);

  const pending = fileBlobInFlight.get(requestUrl);
  if (pending) return pending;

  const request = FetchWithTokenRefresh(requestUrl)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Failed to fetch private file");
      }
      return response.blob();
    })
    .finally(() => fileBlobInFlight.delete(requestUrl));

  fileBlobInFlight.set(requestUrl, request);
  return request;
};

export const FetchPrivateFileObjectUrl = async (url: string) => {
  if (!url || !IsPrivateFileUrl(url)) return url || "";
  const blob = await FetchFileBlob(url);
  return URL.createObjectURL(blob);
};
